interface TelegramWebApp {
  initData: string;
  colorScheme?: "light" | "dark";
  ready(): void;
  expand(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  disableVerticalSwipes?(): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string): void;
  openInvoice?(url: string, callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void): void;
  close?(): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function telegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function initializeTelegram(): TelegramWebApp | null {
  const webApp = telegramWebApp();
  if (!webApp) return null;
  webApp.ready();
  webApp.expand();
  if (!webApp.initData) return webApp;
  webApp.disableVerticalSwipes?.();
  webApp.setHeaderColor?.("#f5f7f5");
  webApp.setBackgroundColor?.("#eef3f1");
  webApp.setBottomBarColor?.("#f7f9f8");
  document.documentElement.dataset.telegram = "true";
  return webApp;
}

export function haptic(type: "light" | "medium" | "success" | "warning") {
  const webApp = telegramWebApp();
  if (!webApp?.initData) return;
  const feedback = webApp.HapticFeedback;
  if (!feedback) return;
  if (type === "success" || type === "warning") feedback.notificationOccurred(type);
  else feedback.impactOccurred(type);
}

export function openBotChat(): boolean {
  const username = import.meta.env.VITE_PUBLIC_BOT_USERNAME?.trim();
  if (!username) return false;
  const url = `https://t.me/${username.replace(/^@/, "")}`;
  const webApp = telegramWebApp();
  if (webApp?.openTelegramLink) webApp.openTelegramLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

export function closeToBotChat(): boolean {
  const webApp = telegramWebApp();
  if (webApp?.initData && webApp.close) {
    webApp.close();
    return true;
  }
  return openBotChat();
}

export function openTelegramInvoice(
  url: string,
  callback: (status: "paid" | "cancelled" | "failed" | "pending") => void
): boolean {
  const webApp = telegramWebApp();
  if (!webApp?.initData || !webApp.openInvoice) return false;
  webApp.openInvoice(url, callback);
  return true;
}

export function openExternalLink(url: string): void {
  const webApp = telegramWebApp();
  if (webApp?.openLink) webApp.openLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

export function shareTelegramLink(url: string, text: string): void {
  const target = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  const webApp = telegramWebApp();
  if (webApp?.openTelegramLink) webApp.openTelegramLink(target);
  else window.open(target, "_blank", "noopener,noreferrer");
}
